import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Save, X, ListChecks, ChevronDown, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PHASES = [
  "Phase 1 - Plan",
  "Phase 2 - Product Design & Development",
  "Phase 3 - Process Design & Development",
  "Phase 4 - Product & Process Validation",
  "Phase 5 - Feedback, Assessment & Corrective Action",
];

const ROLE_LABELS = {
  admin: "Administrator",
  npd_manager: "NPD Manager",
  npd_engineer: "NPD Engineer",
};

function emptyForm() {
  return {
    phase: PHASES[0], seq: 1, task_name: "", department: "",
    default_responsibility_role: "", secondary_responsibility_role: "", default_duration_days: "",
    dependency_seq: "", deliverables: [], active: true,
  };
}

export default function ApqpMaster() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("apqp.manage_master");
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openPhases, setOpenPhases] = useState(() => new Set(PHASES));
  const [view, setView] = useState("tasks");   // "tasks" | "kakotora"
  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);   // all roles → Default Responsibility dropdown

  async function load() {
    setBusy(true);
    try {
      const r = await api.get("/apqp/master");
      setTasks(r.data || []);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/roles").then((r) => setRoles(Array.isArray(r.data) ? r.data : (r.data?.roles || []))).catch(() => {}); }, []);
  const roleLabel = (rid) => { const m = (roles || []).find((x) => x.role_id === rid); return m ? (m.name || m.role_id) : (ROLE_LABELS[rid] || rid); };

  const grouped = PHASES.map((ph) => ({ phase: ph, rows: tasks.filter((t) => t.phase === ph) }));

  function startNew() {
    const maxSeq = tasks.length ? Math.max(...tasks.map((t) => t.seq || 0)) : 0;
    setEditing({ ...emptyForm(), seq: maxSeq + 1, _isNew: true });
    setError("");
  }
  function startEdit(t) {
    setEditing({
      ...t,
      department: t.department || "",
      default_responsibility_role: t.default_responsibility_role || "",
      secondary_responsibility_role: t.secondary_responsibility_role || "",
      default_duration_days: t.default_duration_days ?? "",
      dependency_seq: t.dependency_seq ?? "",
      deliverables: t.deliverables || [],
    });
    setError("");
  }

  async function save() {
    if (!editing.task_name?.trim()) { setError("Task name is required"); return; }
    setBusy(true); setError("");
    try {
      const body = {
        phase: editing.phase,
        seq: Number(editing.seq) || 1,
        task_name: editing.task_name.trim(),
        department: editing.department?.trim() || null,
        default_responsibility_role: editing.default_responsibility_role || null,
        secondary_responsibility_role: editing.secondary_responsibility_role || null,
        default_duration_days: editing.default_duration_days === "" ? null : Number(editing.default_duration_days),
        dependency_seq: editing.dependency_seq === "" ? null : Number(editing.dependency_seq),
        deliverables: (editing.deliverables || []).filter((d) => d.name?.trim()).map((d) => ({ name: d.name.trim(), document_no: d.document_no || "", mandatory: !!d.mandatory })),
        active: !!editing.active,
      };
      if (editing._isNew) await api.post("/apqp/master", body);
      else await api.put(`/apqp/master/${editing.task_id}`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  async function del(t) {
    if (!window.confirm(`Deactivate master task "${t.task_name}"? It will stop cloning into new APQP projects.`)) return;
    setBusy(true);
    try { await api.delete(`/apqp/master/${t.task_id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  function togglePhase(ph) {
    setOpenPhases((prev) => {
      const n = new Set(prev);
      n.has(ph) ? n.delete(ph) : n.add(ph);
      return n;
    });
  }

  function addDeliverable() {
    setEditing({ ...editing, deliverables: [...(editing.deliverables || []), { name: "", document_no: "", mandatory: false }] });
  }
  function updDeliverable(i, patch) {
    setEditing({ ...editing, deliverables: editing.deliverables.map((d, idx) => idx === i ? { ...d, ...patch } : d) });
  }
  function rmDeliverable(i) {
    setEditing({ ...editing, deliverables: editing.deliverables.filter((_, idx) => idx !== i) });
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="apqp-master-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks size={22} className="text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              APQP Master <span className="text-base font-medium text-slate-400">({tasks.length})</span>
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Phase-grouped master task list. New APQP projects clone every <strong>active</strong> task in phase order.
            </p>
          </div>
        </div>
        {canManage && view === "tasks" && (
          <button onClick={startNew} data-testid="apqp-master-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold">
            <Plus size={14} /> Add APQP Master
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        {[["tasks", "Master Tasks"], ["kakotora", "Past Defect History (Kakotora)"], ["matrix", "APQP Matrix"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${view === v ? "border-orange-600 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {view === "kakotora" && <KakotoraMaster units={units} canManage={canManage} />}
      {view === "matrix" && <ApqpMatrix canManage={canManage} />}

      {view === "tasks" && (
      <div className="space-y-3">
        {grouped.map(({ phase, rows }) => (
          <div key={phase} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`apqp-phase-group-${phase}`}>
            <button onClick={() => togglePhase(phase)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left">
              {openPhases.has(phase) ? <ChevronDown size={15} className="text-slate-500" /> : <ChevronRight size={15} className="text-slate-500" />}
              <span className="font-semibold text-sm text-slate-800">{phase}</span>
              <span className="text-xs text-slate-400 ml-1">({rows.length})</span>
            </button>
            {openPhases.has(phase) && (
              <table className="w-full text-sm">
                <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2 w-12">Seq</th>
                    <th className="text-left px-4 py-2">Task</th>
                    <th className="text-left px-4 py-2 w-24">Dep. Seq</th>
                    <th className="text-left px-4 py-2 w-24">Duration</th>
                    <th className="text-left px-4 py-2 w-20">Active</th>
                    <th className="text-right px-4 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-4 text-center text-xs text-slate-400">No tasks in this phase yet.</td></tr>
                  )}
                  {rows.map((t) => (
                    <tr key={t.task_id} className={`hover:bg-slate-50 ${!t.active ? "opacity-50" : ""}`} data-testid={`apqp-master-row-${t.task_id}`}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500">{t.seq}</td>
                      <td className="px-4 py-2 text-slate-800">{t.task_name}
                        {t.department && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.department}</span>}
                        {t.default_responsibility_role && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700" title="Primary responsibility">{roleLabel(t.default_responsibility_role)}</span>
                        )}
                        {t.secondary_responsibility_role && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-500 ring-1 ring-indigo-100" title="Secondary responsibility">2nd: {roleLabel(t.secondary_responsibility_role)}</span>
                        )}
                        {(t.deliverables || []).length > 0 && (
                          <span className="ml-2 text-[10px] text-sky-600">· {t.deliverables.length} deliverable(s)</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-500">{t.dependency_seq ?? "—"}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{t.default_duration_days != null ? `${t.default_duration_days} d` : "—"}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`inline-block px-1.5 py-0.5 rounded ${t.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                          {t.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {canManage && (
                          <>
                            <button onClick={() => startEdit(t)} className="text-sky-600 hover:text-sky-700 p-1 mr-1" title="Edit" data-testid={`apqp-master-edit-${t.task_id}`}>
                              <Edit size={14} />
                            </button>
                            <button onClick={() => del(t)} className="text-slate-400 hover:text-red-600 p-1" title="Deactivate" data-testid={`apqp-master-delete-${t.task_id}`}>
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" data-testid="apqp-master-editor">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">{editing._isNew ? "New APQP Master Task" : "Edit APQP Master Task"}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            {error && <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">Phase</label>
              <select value={editing.phase} onChange={(e) => setEditing({ ...editing, phase: e.target.value })}
                className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm bg-white" data-testid="apqp-master-phase">
                {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">Task Name *</label>
              <input value={editing.task_name} onChange={(e) => setEditing({ ...editing, task_name: e.target.value })}
                className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" autoFocus data-testid="apqp-master-name" />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Seq</label>
                <input type="number" value={editing.seq} onChange={(e) => setEditing({ ...editing, seq: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="apqp-master-seq" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Dependency Seq</label>
                <input type="number" value={editing.dependency_seq} onChange={(e) => setEditing({ ...editing, dependency_seq: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" placeholder="—" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Duration (days)</label>
                <input type="number" value={editing.default_duration_days} onChange={(e) => setEditing({ ...editing, default_duration_days: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" placeholder="—" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Department</label>
                <input value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" placeholder="e.g. NPD, CFT, Quality" data-testid="apqp-master-department" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Primary Responsibility Role</label>
                <select value={editing.default_responsibility_role} onChange={(e) => setEditing({ ...editing, default_responsibility_role: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm bg-white">
                  <option value="">—</option>
                  {(roles && roles.length
                    ? roles.map((r) => ({ id: r.role_id, label: r.name || r.role_id }))
                    : [{ id: "admin", label: "Administrator" }, { id: "npd_manager", label: "NPD Manager" }, { id: "npd_engineer", label: "NPD Engineer" }]
                  ).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Secondary Responsibility Role <span className="text-slate-400">(optional)</span></label>
                <select value={editing.secondary_responsibility_role} onChange={(e) => setEditing({ ...editing, secondary_responsibility_role: e.target.value })}
                  className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm bg-white" data-testid="apqp-master-secondary-role">
                  <option value="">—</option>
                  {(roles && roles.length
                    ? roles.map((r) => ({ id: r.role_id, label: r.name || r.role_id }))
                    : [{ id: "admin", label: "Administrator" }, { id: "npd_manager", label: "NPD Manager" }, { id: "npd_engineer", label: "NPD Engineer" }]
                  ).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="h-4 w-4 accent-orange-600" data-testid="apqp-master-active" />
                Active
              </label>
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-slate-500">Deliverables <span className="text-slate-400">(Name · Document No. · Mandatory)</span></label>
                <button type="button" onClick={addDeliverable} className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"><Plus size={12} /> Add</button>
              </div>
              {(editing.deliverables || []).length === 0 && <div className="text-xs text-slate-400 italic">No deliverables.</div>}
              {(editing.deliverables || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <input value={d.name} onChange={(e) => updDeliverable(i, { name: e.target.value })}
                    placeholder="Deliverable name" className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm" data-testid={`apqp-master-deliv-name-${i}`} />
                  <input value={d.document_no || ""} onChange={(e) => updDeliverable(i, { document_no: e.target.value })}
                    placeholder="Doc No." className="w-28 border border-slate-200 rounded px-2 py-1 text-sm" data-testid={`apqp-master-deliv-docno-${i}`} />
                  <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                    <input type="checkbox" checked={!!d.mandatory} onChange={(e) => updDeliverable(i, { mandatory: e.target.checked })} className="accent-orange-600" /> mandatory
                  </label>
                  <button type="button" onClick={() => rmDeliverable(i)} className="text-slate-400 hover:text-red-600 p-1"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button onClick={() => setEditing(null)} disabled={busy} className="px-3 py-1.5 text-sm border border-slate-300 rounded">Cancel</button>
              <button onClick={save} disabled={busy || !editing.task_name?.trim()} data-testid="apqp-master-save-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Past Defect History (Kakotora) master — unit-scoped entries ----------
const KK_COLS = [
  ["project", "Project", "w-24"], ["part_name", "Part Name/No", "min-w-[130px]"],
  ["complaint", "Complaint/Incident", "min-w-[150px]"], ["customer", "Customer/Supp.", "w-28"],
  ["date", "Date", "w-20"], ["category", "Category", "w-24"],
  ["root_cause", "Root Cause", "min-w-[170px]"], ["lesson_learnt", "Lesson Learnt", "min-w-[170px]"],
  ["updated_by", "Updated By", "w-24"], ["applicability", "Applicable", "w-20"],
];

function kkEmpty(units = ["all"]) {
  return { project: "", part_name: "", complaint: "", customer: "", date: "", category: "",
    root_cause: "", lesson_learnt: "", updated_by: "", applicability: "YES", remarks: "", units };
}

function KakotoraMaster({ units, canManage }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filterUnit, setFilterUnit] = useState("");   // "" = all, "all" = combined only, or unit_id

  async function load() {
    setBusy(true);
    try { const r = await api.get("/apqp/master/kakotora"); setRows(r.data || []); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  const unitLabel = (u) => u.unit_code || u.unit_name || u.unit_id;
  const scopeText = (e) => {
    const us = e.units || [];
    if (!us.length || us.includes("all")) return "Combined (all units)";
    return us.map((id) => { const u = units.find((x) => x.unit_id === id); return u ? unitLabel(u) : id; }).join(", ");
  };
  const visible = rows.filter((e) => {
    if (!filterUnit) return true;
    const us = e.units || [];
    const combined = !us.length || us.includes("all");
    if (filterUnit === "all") return combined;
    return combined || us.includes(filterUnit);
  });

  async function save() {
    setError("");
    if (!editing.part_name && !editing.complaint) { setError("Enter at least Part Name or Complaint"); return; }
    setBusy(true);
    try {
      const body = { ...editing };
      if (editing._isNew) await api.post("/apqp/master/kakotora", body);
      else await api.put(`/apqp/master/kakotora/${editing.id}`, body);
      setEditing(null); await load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }
  async function del(e) {
    if (!window.confirm("Delete this defect entry from the master?")) return;
    await api.delete(`/apqp/master/kakotora/${e.id}`); await load();
  }
  async function importExcel(ev, unitId) {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    const scope = units.find((u) => u.unit_id === unitId);
    const replace = window.confirm(`Import defect rows from "${f.name}" into ${scope ? unitLabel(scope) : "Combined"}.\n\nOK = REPLACE this list with the file's rows.\nCancel = ADD the file's rows to the existing list.`);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/apqp/master/kakotora/import?unit_id=${encodeURIComponent(unitId || "")}&replace=${replace}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${r.data.created} defect rows.`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  function toggleUnit(uid) {
    setEditing((c) => {
      let us = [...(c.units || [])];
      if (uid === "all") return { ...c, units: ["all"] };
      us = us.filter((x) => x !== "all");
      us = us.includes(uid) ? us.filter((x) => x !== uid) : [...us, uid];
      return { ...c, units: us.length ? us : ["all"] };
    });
  }

  const fld = "w-full border border-slate-200 rounded px-2 py-1.5 text-sm";

  return (
    <div>
      {(() => { const fu = units.find((u) => u.unit_id === filterUnit); const isUnit = !!fu; const scopeLabel = isUnit ? unitLabel(fu) : (filterUnit === "all" ? "Combined" : "All units"); return (
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">
          Master list of past defects — one list per unit. Pick a unit below to see &amp; edit that unit's Kakotora sheet
          ({isUnit ? <><strong>{scopeLabel}</strong> + Combined entries apply</> : "unit-specific + Combined entries"}); the matching unit's APQP auto-fills from it.
          <span className="text-slate-400"> ({visible.length} shown / {rows.length} total)</span>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer whitespace-nowrap">
              <Save size={14} /> Import from Excel
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => importExcel(e, isUnit ? filterUnit : "")} />
            </label>
            <button onClick={() => setEditing({ ...kkEmpty(isUnit ? [filterUnit] : ["all"]), _isNew: true })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold whitespace-nowrap">
              <Plus size={14} /> Add to {scopeLabel}
            </button>
          </div>
        )}
      </div>
      ); })()}
      <div className="text-[11px] text-slate-400 mb-2">To load a unit's list quickly, select the unit above (e.g. <strong>Kakotora — {units[1] ? unitLabel(units[1]) : "Unit 3"}</strong>) then <strong>Import from Excel</strong> — upload that unit's Kakotora (.xlsx) and it fills that unit's list.</div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-slate-400 uppercase tracking-wide">Kakotora list</span>
        <select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm bg-white">
          <option value="">All entries</option>
          <option value="all">Combined (all units)</option>
          {units.map((u) => <option key={u.unit_id} value={u.unit_id}>Kakotora — {unitLabel(u)}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="text-left px-2 py-2 w-8">#</th>
              {KK_COLS.map(([k, l]) => <th key={k} className="text-left px-2 py-2">{l}</th>)}
              <th className="text-left px-2 py-2 w-40">Units</th>
              {canManage && <th className="text-right px-2 py-2 w-20"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((e, i) => (
              <tr key={e.id} className="hover:bg-slate-50 align-top">
                <td className="px-2 py-2 text-slate-400">{i + 1}</td>
                {KK_COLS.map(([k]) => <td key={k} className="px-2 py-2 text-slate-700"><div className="max-w-[220px] whitespace-normal break-words line-clamp-3">{e[k]}</div></td>)}
                <td className="px-2 py-2">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${(!e.units?.length || e.units?.includes("all")) ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{scopeText(e)}</span>
                </td>
                {canManage && (
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...e, _isNew: false })} className="text-slate-400 hover:text-orange-600 mr-2"><Edit size={14} /></button>
                    <button onClick={() => del(e)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={KK_COLS.length + 3} className="px-3 py-6 text-center text-slate-400">No entries.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">{editing._isNew ? "New Defect Entry" : "Edit Defect Entry"}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            {error && <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-slate-500 mb-1">Project</label><input className={fld} value={editing.project} onChange={(e) => setEditing({ ...editing, project: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Part Name / No.</label><input className={fld} value={editing.part_name} onChange={(e) => setEditing({ ...editing, part_name: e.target.value })} /></div>
              <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Complaint / Incident</label><textarea rows={2} className={fld} value={editing.complaint} onChange={(e) => setEditing({ ...editing, complaint: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Customer / Supplier</label><input className={fld} value={editing.customer} onChange={(e) => setEditing({ ...editing, customer: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Date</label><input className={fld} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Category</label><input className={fld} placeholder="Internal / External / Supplier / NPD" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Updated By</label><input className={fld} value={editing.updated_by} onChange={(e) => setEditing({ ...editing, updated_by: e.target.value })} /></div>
              <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Root Cause / Reason</label><textarea rows={3} className={fld} value={editing.root_cause} onChange={(e) => setEditing({ ...editing, root_cause: e.target.value })} /></div>
              <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Lesson Learnt (Counter Measure)</label><textarea rows={3} className={fld} value={editing.lesson_learnt} onChange={(e) => setEditing({ ...editing, lesson_learnt: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Applicability on Current Project</label><select className={`${fld} bg-white`} value={editing.applicability} onChange={(e) => setEditing({ ...editing, applicability: e.target.value })}><option value="YES">YES</option><option value="NO">NO</option></select></div>
              <div><label className="block text-xs text-slate-500 mb-1">Remarks</label><input className={fld} value={editing.remarks} onChange={(e) => setEditing({ ...editing, remarks: e.target.value })} /></div>
            </div>

            <div className="mt-4">
              <label className="block text-xs text-slate-500 mb-1.5">Applies to units</label>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => toggleUnit("all")}
                  className={`px-2.5 py-1 text-xs rounded-full border ${(!editing.units?.length || editing.units?.includes("all")) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"}`}>
                  Combined (all units)
                </button>
                {units.map((u) => {
                  const on = (editing.units || []).includes(u.unit_id);
                  return (
                    <button key={u.unit_id} type="button" onClick={() => toggleUnit(u.unit_id)}
                      className={`px-2.5 py-1 text-xs rounded-full border ${on ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-600 border-slate-300"}`}>
                      {unitLabel(u)}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Pick "Combined" to show in every unit, or select one/more specific units (e.g. Unit-1 and Unit-3).</div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-semibold disabled:opacity-50">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- APQP Matrix master (ENGG-F-07) — dev-category applicability + responsibilities ----------
const MX_PHASES = [
  "Phase 1 - Plan", "Phase 2 - Product Design & Development", "Phase 3 - Process Design & Development",
  "Phase 4 - Product & Process Validation", "Phase 5 - Feedback, Assessment & Corrective Action",
];
const MX_CATS = [["A", "NPD"], ["B", "SPD"], ["C", "ECN"], ["D", "PCN"], ["E", "Proto"], ["F", "PLC/FLC"], ["G", "SC"], ["H", "C-Mfg"]];
const MX_RESP = [["mktg", "Mktg"], ["npd_engg", "NPD/Eng"], ["qa", "QA"], ["prod", "Prod"], ["tool_room", "Tool Rm"], ["hr", "HR"], ["purchase", "Purch"], ["scm", "SCM"]];
const MX_CAT_OPTS = [["", "—"], ["point", "○ Point"], ["review", "● Review"], ["if_required", "If req"]];
const MX_RESP_OPTS = [["", "—"], ["prime", "◎ Prime"], ["co", "○ Co"]];

function ApqpMatrix({ canManage }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(false);

  async function load() {
    setBusy(true);
    try { const r = await api.get("/apqp/master/matrix"); setRows(r.data || []); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  // Autosave a row after an inline edit.
  async function saveRow(row, patch) {
    const next = { ...row, ...patch };
    setRows((rs) => rs.map((x) => (x.id === row.id ? next : x)));
    try {
      await api.put(`/apqp/master/matrix/${row.id}`, {
        task_name: next.task_name, phase: next.phase, seq: next.seq,
        categories: next.categories || {}, responsibilities: next.responsibilities || {},
        remarks: next.remarks || "", document_reference: next.document_reference || "",
      });
    } catch (e) { /* keep local; user can retry */ }
  }
  const setCat = (row, key, val) => saveRow(row, { categories: { ...(row.categories || {}), [key]: val } });
  const setResp = (row, key, val) => saveRow(row, { responsibilities: { ...(row.responsibilities || {}), [key]: val } });

  async function addRow(phase) {
    const seq = Math.max(0, ...rows.filter((r) => r.phase === phase).map((r) => r.seq || 0)) + 1;
    await api.post("/apqp/master/matrix", { task_name: "New task", phase, seq, categories: {}, responsibilities: {} });
    await load();
  }
  async function del(row) {
    if (!window.confirm(`Delete matrix row "${row.task_name}"?`)) return;
    await api.delete(`/apqp/master/matrix/${row.id}`); await load();
  }
  async function download() {
    const r = await api.get("/apqp/master/matrix.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "APQP Matrix.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }

  const sel = "border border-slate-200 rounded px-0.5 py-0.5 text-[11px] bg-white w-full";
  const inp = "w-full border border-slate-200 rounded px-1 py-0.5 text-xs";
  const byPhase = MX_PHASES.map((ph) => ({ ph, list: rows.filter((r) => r.phase === ph) }));

  const inner = (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">
          Per-task development-category applicability (A–H) and department responsibilities. Drives which tasks apply per Dev Category in each project.
          <span className="text-slate-400"> ({rows.length} tasks)</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setFull((f) => !f)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50" title={full ? "Exit full screen" : "Full screen (see all columns)"}>
            {full ? <Minimize2 size={14} /> : <Maximize2 size={14} />} {full ? "Exit full screen" : "Full screen"}
          </button>
          <button onClick={download} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50">
            <Save size={14} /> Download Excel
          </button>
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mb-2">Category cell: ○ Point · ● Review · If req · — N/A.  Responsibility: ◎ Prime · ○ Co.</div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              <th className="text-left px-2 py-2 w-8">#</th>
              <th className="text-left px-2 py-2 min-w-[220px]">Task</th>
              {MX_CATS.map(([k, l]) => <th key={k} className="px-1 py-2 w-14" title={l}>{k}<div className="normal-case text-[8px] text-slate-400">{l}</div></th>)}
              {MX_RESP.map(([k, l]) => <th key={k} className="px-1 py-2 w-16">{l}</th>)}
              <th className="text-left px-2 py-2 min-w-[120px]">Doc Ref</th>
              {canManage && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {byPhase.map(({ ph, list }) => (
              list.length > 0 && (
                <>
                  <tr key={ph} className="bg-yellow-50">
                    <td colSpan={2 + MX_CATS.length + MX_RESP.length + 1 + (canManage ? 1 : 0)} className="px-2 py-1.5 font-semibold text-slate-700">
                      {ph}
                      {canManage && <button onClick={() => addRow(ph)} className="ml-3 text-[11px] text-orange-600 hover:underline inline-flex items-center gap-0.5"><Plus size={11} /> Add task</button>}
                    </td>
                  </tr>
                  {list.map((row, i) => (
                    <tr key={row.id} className="hover:bg-slate-50 align-top">
                      <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                      <td className="px-2 py-1">
                        <input className={inp} disabled={!canManage} value={row.task_name || ""} onChange={(e) => setRows((rs) => rs.map((x) => x.id === row.id ? { ...x, task_name: e.target.value } : x))} onBlur={(e) => saveRow(row, { task_name: e.target.value })} />
                      </td>
                      {MX_CATS.map(([k]) => (
                        <td key={k} className="px-0.5 py-1">
                          <select className={sel} disabled={!canManage} value={(row.categories || {})[k] || ""} onChange={(e) => setCat(row, k, e.target.value)}>
                            {MX_CAT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                      ))}
                      {MX_RESP.map(([k]) => (
                        <td key={k} className="px-0.5 py-1">
                          <select className={sel} disabled={!canManage} value={(row.responsibilities || {})[k] || ""} onChange={(e) => setResp(row, k, e.target.value)}>
                            {MX_RESP_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                      ))}
                      <td className="px-2 py-1">
                        <input className={inp} disabled={!canManage} value={row.document_reference || ""} onChange={(e) => setRows((rs) => rs.map((x) => x.id === row.id ? { ...x, document_reference: e.target.value } : x))} onBlur={(e) => saveRow(row, { document_reference: e.target.value })} />
                      </td>
                      {canManage && <td className="px-1 py-1 text-center"><button onClick={() => del(row)} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button></td>}
                    </tr>
                  ))}
                </>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (full) return <div className="fixed inset-0 z-50 bg-white overflow-auto p-4">{inner}</div>;
  return inner;
}
